import { createSlice } from '@reduxjs/toolkit';
import { fetchWebhooks, createWebhook, updateWebhook, deleteWebhook } from './webhooksThunks';

/**
 * The seller's webhooks, which the legacy kept in `useState` inside
 * `WebhookMain`, plus what the listing and the detail page share.
 *
 * Same list rules as the legacy: a created webhook goes first, an edited one is
 * replaced by id and a deleted one is dropped, all without asking the backend
 * for the list again. Save and delete errors are not stored in `error`: they
 * are toasts, see `webhooksFeedback`.
 */
export const initialFilters = {
  search: '',
  status: '',
  page: 1,
  rowsPerPage: 20,
};

const initialState = {
  webhooks: [],
  loading: false,
  saving: false,
  error: null,
  /** Kept in the store rather than the page, so the filters survive a visit to a detail. */
  filters: initialFilters,
};

const webhooksSlice = createSlice({
  name: 'webhooks',
  initialState,
  reducers: {
    /** Any change other than the page itself goes back to the first page. */
    setWebhookFilters: (state, action) => {
      state.filters = { ...state.filters, page: 1, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWebhooks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWebhooks.fulfilled, (state, action) => {
        state.loading = false;
        state.webhooks = action.payload || [];
      })
      .addCase(fetchWebhooks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createWebhook.pending, (state) => {
        state.saving = true;
      })
      .addCase(createWebhook.fulfilled, (state, action) => {
        state.saving = false;
        state.webhooks = [action.payload, ...state.webhooks];
      })
      .addCase(createWebhook.rejected, (state) => {
        state.saving = false;
      })

      .addCase(updateWebhook.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateWebhook.fulfilled, (state, action) => {
        state.saving = false;
        state.webhooks = state.webhooks.map((webhook) =>
          webhook.id === action.payload.id ? action.payload : webhook
        );
      })
      .addCase(updateWebhook.rejected, (state) => {
        state.saving = false;
      })

      .addCase(deleteWebhook.fulfilled, (state, action) => {
        state.webhooks = state.webhooks.filter((webhook) => webhook.id !== action.payload);
      });
  },
});

export const { setWebhookFilters } = webhooksSlice.actions;

export default webhooksSlice.reducer;
